import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MessageService } from './message.service';

@Injectable({ providedIn: 'root' })
export class ManifesteService {

  constructor(private http: HttpClient, private messageService: MessageService) {}

  private url = `${environment.apiurl}manifestes`;
  private cmdUrl = `${environment.apiurl}commandes`;

  listerManifestes(page = 0, size = 20): Observable<any> {
    return this.http.get<any>(this.url, { params: { page, size } })
      .pipe(catchError(this.messageService.gererErreur));
  }

  obtenirManifeste(id: number): Observable<any> {
    return this.http.get<any>(`${this.url}/${id}`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  creerManifeste(data: any): Observable<any> {
    return this.http.post<any>(`${this.url}/enregistrerManifeste`, data)
      .pipe(catchError(this.messageService.gererErreur));
  }

  modifierManifeste(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${this.url}/${id}`, data)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Change le statut d'un manifeste via PATCH /{id}/statut */
  changerStatut(id: number, statut: string): Observable<any> {
    return this.http.patch<any>(`${this.url}/${id}/statut`, { statut })
      .pipe(catchError(this.messageService.gererErreur));
  }

  consignerReception(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${this.url}/${id}/consigner-reception`, data)
      .pipe(catchError(this.messageService.gererErreur));
  }

  supprimerManifeste(id: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${id}`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  rechercherManifestes(params: {
    numeroReference?: string; centreTri?: string; status?: string;
    page?: number; size?: number;
  }): Observable<any> {
    const qp: any = { page: params.page ?? 0, size: params.size ?? 20 };
    if (params.numeroReference) qp['numeroReference'] = params.numeroReference;
    if (params.centreTri) qp['centreTri'] = params.centreTri;
    if (params.status) qp['status'] = params.status;
    return this.http.get<any>(`${this.url}/rechercher`, { params: qp })
      .pipe(catchError(this.messageService.gererErreur));
  }

  // ── Gestion des commandes liées ──────────────────────────────────────────────

  obtenirCommandesManifeste(manifesteId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/${manifesteId}/commandes`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  ajouterCommandes(manifesteId: number, commandeIds: number[]): Observable<any> {
    return this.http.post<any>(`${this.url}/${manifesteId}/commandes`, commandeIds)
      .pipe(catchError(this.messageService.gererErreur));
  }

  retirerCommande(manifesteId: number, commandeId: number): Observable<any> {
    return this.http.delete<any>(`${this.url}/${manifesteId}/commandes/${commandeId}`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Commandes STANDARD_INTERURBAIN sans manifeste — éligibles pour regroupement.
   *  Les zones sont optionnelles : si fournies, filtre les commandes correspondant au trajet. */
  obtenirCommandesEligibles(zoneDepartId?: number, zoneArriveeId?: number): Observable<any[]> {
    const params: any = {};
    if (zoneDepartId) params['zoneDepartId'] = zoneDepartId;
    if (zoneArriveeId) params['zoneArriveeId'] = zoneArriveeId;
    return this.http.get<any[]>(`${this.cmdUrl}/interurbain/eligibles-manifeste`, { params })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Recherche une commande par son numéro (recherche globale, hors critères d'éligibilité). */
  rechercherCommandeParNumero(numero: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.cmdUrl}/search/numero/${encodeURIComponent(numero)}`)
      .pipe(catchError(this.messageService.gererErreur));
  }

  /**
   * Réconciliation : zone d'arrivée constate ligne par ligne ce qui a été reçu.
   * Passe le manifeste en RECEPTIONNE et met à jour chaque commande.
   */
  reconcilier(manifesteId: number, lignes: {
    commandeId: number; numeroCommande: string; etat: 'RECU_OK' | 'MANQUANT' | 'ENDOMMAGE'; observations?: string;
  }[], observations?: string): Observable<any> {
    return this.http.post<any>(`${this.url}/${manifesteId}/reconciliation`, { lignes, observations })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /**
   * Affectation last-mile : assigne des livreurs aux commandes réceptionnées.
   */
  affecterLivraisons(manifesteId: number, affectations: { commandeId: number; livreurId: number }[]): Observable<any> {
    return this.http.post<any>(`${this.url}/${manifesteId}/affecter-livraisons`, { affectations })
      .pipe(catchError(this.messageService.gererErreur));
  }

  /** Récupère les livreurs disponibles pour une zone donnée. */
  livreursParZone(zoneId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/livreurs-zone/${zoneId}`)
      .pipe(catchError(this.messageService.gererErreur));
  }
}
